import type { Plan } from '@cel_cash/core/types'
import { z } from 'zod'
import ApiService from '../api.service'

/**
 * Periodicidades aceitas pelo PlanoController.
 * Mantém paridade com o enum `Periodicidade` da API e com os rótulos em
 * `locales/plan.ts`.
 */
export const PERIODICIDADES = [
    'weekly',
    'biweekly',
    'monthly',
    'bimonthly',
    'quarterly',
    'biannual',
    'yearly',
] as const satisfies readonly Plan['periodicity'][]

/**
 * Schema do formulário de plano. O usuário informa o preço em reais
 * (`precoReais`) e o schema converte para centavos (`price`), formato esperado
 * pelo PlanoController.
 */
export const createPlanoSchema = z
    .object({
        periodicity: z.enum(PERIODICIDADES, {
            errorMap: () => ({ message: 'Selecione a periodicidade' }),
        }),
        limiteFuncionarios: z.coerce
            .number({ invalid_type_error: 'Informe o limite de funcionários' })
            .int('Deve ser um número inteiro')
            .min(0, 'Não pode ser negativo'),
        precoReais: z.preprocess(
            value => {
                if (typeof value !== 'string') return value
                // Normaliza o formato pt-BR do Cleave ("1.234,56") para número.
                const normalized = value.replace(/\./g, '').replace(',', '.')
                return normalized === '' ? undefined : Number(normalized)
            },
            z
                .number({ invalid_type_error: 'Informe o preço' })
                .min(0, 'Não pode ser negativo'),
        ),
    })
    .transform(({ precoReais, ...rest }) => ({
        ...rest,
        price: Math.round(precoReais * 100),
    }))

export const updatePlanoSchema = createPlanoSchema

/** Corpo enviado ao criar/atualizar um plano (após transformação do schema). */
export type CreatePlanoInput = z.infer<typeof createPlanoSchema>

export type PlanoFixo = {
    id: number
    limiteFuncionarios: number
    periodicity: Plan['periodicity']
    price: number
    /**
     * Identificador do plano no sistema legado
     * @deprecated Use `limiteFuncionarios` e `periodicity` para identificar o plano
     * @example '10-monthly'
     */
    legacyMyId?: string
}

export const planos = new ApiService<CreatePlanoInput, PlanoFixo>('planos')

export const faixaColaborador = new ApiService<number>(
    'planos/faixa-colaboradores',
)
